import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import {
  isVerifiedGoogleUser,
} from "@/lib/auth/google-access";
import { getFirebaseServices } from "@/lib/firebase/client";
import { ensureUserProfile } from "@/services/user-service";
import { getFriendlyAuthError } from "./auth-errors";
import { AuthContext, type AuthContextValue } from "./auth-context";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const firebase = getFirebaseServices();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(firebase));
  const [error, setError] = useState<string | null>(
    firebase
      ? null
      : "Firebase is not configured. Copy .env.example to .env and add your web app settings.",
  );

  useEffect(() => {
    if (!firebase) {
      return;
    }

    void setPersistence(firebase.auth, browserLocalPersistence);
    return onAuthStateChanged(
      firebase.auth,
      (nextUser) => {
        void (async () => {
          if (!nextUser) {
            setUser(null);
            setLoading(false);
            return;
          }

          try {
            if (!isVerifiedGoogleUser(nextUser)) {
              throw new Error(
                "Master Notes requires a Google account with a verified email address.",
              );
            }
            await ensureUserProfile(firebase.db, nextUser);
            setUser(nextUser);
            setError(null);
          } catch (profileError) {
            await firebaseSignOut(firebase.auth);
            setUser(null);
            setError(getFriendlyAuthError(profileError));
          } finally {
            setLoading(false);
          }
        })();
      },
      (authError) => {
        setError(getFriendlyAuthError(authError));
        setLoading(false);
      },
    );
  }, [firebase]);

  const signInWithGoogle = useCallback(async () => {
    if (!firebase) {
      setError("Firebase is not configured for this environment.");
      return;
    }

    setError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      await signInWithPopup(firebase.auth, provider);
    } catch (signInError) {
      setError(getFriendlyAuthError(signInError));
      throw signInError;
    }
  }, [firebase]);

  const signOut = useCallback(async () => {
    if (firebase) {
      await firebaseSignOut(firebase.auth);
    }
  }, [firebase]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, signInWithGoogle, signOut }),
    [error, loading, signInWithGoogle, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
